import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useCurrentUser } from '@/features/auth/state/auth'
import { Edit, Plus, Trash2, RefreshCw } from 'lucide-react'
import { useFetchProductHistory } from '@/features/products/services'
import { isAdminCeo } from '@/lib/permissions'

// Dados fictícios atualizados para incluir a ação
// const modificacoes = [
//   {
//     id: 1,
//     usuario: 'Maria Silva',
//     cpf: '123.456.789-00',
//     dataHora: '2023-10-08T14:30:00',
//     acao: 'editar',
//     campoModificado: 'Endereço',
//     valorAntigo: 'Rua das Flores, 123',
//     valorNovo: 'Avenida Principal, 456',
//     type: 'PREVIDENCIARIO'
//   },
//   {
//     id: 2,
//     usuario: 'João Santos',
//     cpf: '987.654.321-00',
//     dataHora: '2023-10-07T10:15:00',
//     acao: 'adicionar',
//     campoModificado: 'Telefone',
//     valorAntigo: '',
//     valorNovo: '(11) 91234-5678',
//     type: 'PREVIDENCIARIO'
//   },
//   {
//     id: 3,
//     usuario: 'Ana Oliveira',
//     cpf: '456.789.123-00',
//     dataHora: '2023-10-06T16:45:00',
//     acao: 'excluir',
//     campoModificado: 'E-mail',
//     valorAntigo: 'ana.oliveira@email.com',
//     valorNovo: '',
//     type: 'ICMS'
//   },
//   {
//     id: 4,
//     usuario: 'Carlos Mendes',
//     cpf: '789.123.456-00',
//     dataHora: '2023-10-05T09:20:00',
//     acao: 'atualizar',
//     campoModificado: 'Status',
//     valorAntigo: 'Pendente',
//     valorNovo: 'Aprovado',
//     type: 'Cliente'
//   }
// ]

// Função para obter o ícone baseado na ação
function getActionIcon(acao: string) {
  switch (acao) {
    case 'create':
    case 'adicionar':
      return <Plus className="h-5 w-5 text-green-500" />
    case 'delete':
    case 'excluir':
      return <Trash2 className="h-5 w-5 text-red-500" />
    case 'modify':
    case 'editar':
      return <Edit className="h-5 w-5 text-blue-500" />
    case 'update':
    case 'atualizar':
      return <RefreshCw className="h-5 w-5 text-yellow-500" />
    default:
      return null
  }
}

interface HistoricoProps {
  type: string
  productId?: string
}

type HistoryChange = {
  campo: string
  valorAntigo: unknown
  valorNovo: unknown
}

const NUMERIC_HISTORY_FIELDS = new Set([
  'valueOriginal',
  'valuePago',
  'valueUtilizado',
  'valueSaldo',
  'valueSelic',
  'honorarios',
  'totalCompensado',
  'honorarios_percent',
  'comissao_porcentagem'
])

function toPtBrCompetencia(value: string) {
  if (!/^\d{6}$/.test(value)) return value
  return `${value.slice(0, 2)}/${value.slice(2)}`
}

function parseHistoryNumber(campo: string, value: unknown): number | null {
  if (!NUMERIC_HISTORY_FIELDS.has(campo)) return null

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (typeof value !== 'string') return null

  const trimmed = value.trim()
  if (!trimmed) return null

  const normalized =
    trimmed.includes(',') && trimmed.includes('.')
      ? trimmed.replace(/\./g, '').replace(',', '.')
      : trimmed.includes(',')
        ? trimmed.replace(',', '.')
        : trimmed.split('.').length > 2
          ? trimmed.replace(/\./g, '')
          : trimmed

  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function areHistoryValuesEqual(campo: string, oldValue: unknown, newValue: unknown): boolean {
  const oldNumber = parseHistoryNumber(campo, oldValue)
  const newNumber = parseHistoryNumber(campo, newValue)

  if (oldNumber !== null && newNumber !== null) {
    return oldNumber === newNumber
  }

  return formatHistoryValue(campo, oldValue) === formatHistoryValue(campo, newValue)
}

function formatHistoryValue(campo: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'

  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não'
  }

  const numericValue = parseHistoryNumber(campo, value)
  if (numericValue !== null) {
    return numericValue.toLocaleString('pt-BR')
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toLocaleString('pt-BR') : String(value)
  }

  if (typeof value === 'string') {
    if (campo === 'dateStart' || campo === 'dateEnd') {
      return toPtBrCompetencia(value)
    }
    return value
  }

  if (Array.isArray(value)) {
    if (!value.length) return '—'

    return value
      .map(item => {
        if (item === null || item === undefined) return '—'
        if (typeof item !== 'object') return String(item)

        const nome = (item as Record<string, unknown>).nome ?? (item as Record<string, unknown>).name
        const valor = (item as Record<string, unknown>).value ?? (item as Record<string, unknown>).valor
        if (nome !== undefined || valor !== undefined) {
          return [nome, valor].filter(part => part !== undefined && part !== null && part !== '').join(': ')
        }

        return JSON.stringify(item, null, 2)
      })
      .join('\n')
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }

  return String(value)
}

function buildChanges(details: Record<string, any> | undefined): HistoryChange[] {
  if (!details) return []

  if (details.changes && typeof details.changes === 'object' && !Array.isArray(details.changes)) {
    return Object.entries(details.changes)
      .map(([campo, value]) => ({
        campo,
        valorAntigo:
          value && typeof value === 'object' && ('old' in (value as Record<string, unknown>))
            ? (value as Record<string, unknown>).old ?? '—'
            : '—',
        valorNovo:
          value && typeof value === 'object' && ('new' in (value as Record<string, unknown>))
            ? (value as Record<string, unknown>).new ?? '—'
            : value
      }))
      .filter(item => !areHistoryValuesEqual(item.campo, item.valorAntigo, item.valorNovo))
  }

  const before = details.before && typeof details.before === 'object' ? details.before : {}
  const after = details.after && typeof details.after === 'object' ? details.after : {}
  const fields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]))

  if (fields.length) {
    return fields
      .filter(campo => JSON.stringify(before[campo]) !== JSON.stringify(after[campo]))
      .map(campo => ({
        campo,
        valorAntigo: before[campo] ?? '—',
        valorNovo: after[campo] ?? '—'
      }))
      .filter(item => !areHistoryValuesEqual(item.campo, item.valorAntigo, item.valorNovo))
  }

  return Object.entries(details)
    .filter(([key]) => !['summary', 'before', 'after', 'changes'].includes(key))
    .map(([campo, value]) => ({
      campo,
      valorAntigo: value?.old ?? '—',
      valorNovo: value?.new ?? '—'
    }))
    .filter(item => !areHistoryValuesEqual(item.campo, item.valorAntigo, item.valorNovo))
}

function hasVisibleHistory(mod: any): boolean {
  const acao = String(mod.details?.summary?.acao || mod.action || '').toLowerCase()
  return acao !== 'modify' || buildChanges(mod.details).length > 0
}

export default function HistoricoModificacao({ type, productId }: HistoricoProps) {
  const user = useCurrentUser()
  const { data: modificacoes = [], isLoading } = useFetchProductHistory(productId)
  // Se user não existir ou não tiver as credenciais, retorna null
  if (!isAdminCeo(user)) return null

  const modificacoesFiltradas = Array.isArray(modificacoes)
    ? modificacoes.filter(mod => {
        if (productId) return true
        const summaryType = mod?.details?.summary?.typeServico
        return !summaryType || summaryType === type
      }).filter(hasVisibleHistory)
    : []

  if (isLoading) {
    return (
      <Card className="w-full max-w-full bg-background p-1 mt-4 text-foreground">
        <CardHeader>
          <CardTitle className="text-xl text-foreground">Histórico de Modificações</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-foreground">Carregando histórico...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-full bg-background p-1 mt-4 text-foreground">
      <CardHeader>
        <CardTitle className="text-xl text-foreground">Histórico de Modificações</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-h-[200px] pr-4 overflow-y-auto">
          {modificacoesFiltradas.length > 0 ? (
            modificacoesFiltradas.map((mod, index) => {
              const usuario = mod.actor?.name ?? 'Usuário não identificado'
              const cargo = [mod.actor?.typeCargo, mod.actor?.typeUser].filter(Boolean).join(' / ') || '—'
              const dataHora = mod.timestamp ? new Date(mod.timestamp).toLocaleString('pt-BR') : '—'
              const acao = String(mod.details?.summary?.acao || mod.action || '').toLowerCase()
              const alteracoes = buildChanges(mod.details)
              const campoPrincipal = alteracoes[0]?.campo ?? mod.details?.summary?.acao ?? mod.action ?? '—'

              return (
                <div key={mod.id ?? index} className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="text-lg font-semibold">{usuario}</h3>
                      <p className="text-sm text-muted-foreground">{cargo}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{dataHora}</p>
                  </div>
                  <div className="p-3 rounded-md bg-background-secondary shadow-xl">
                    <div className="flex items-center gap-2 mb-2">
                      {getActionIcon(acao)}
                      <p className="font-medium capitalize">
                        {acao || 'ação'} - {campoPrincipal}
                      </p>
                    </div>
                    {alteracoes.length > 0 ? (
                      <div className="mt-2 space-y-3">
                        {alteracoes.map(item => (
                          <div key={`${mod.id}-${item.campo}`} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <p className="text-sm font-medium">Campo</p>
                              <p className="text-sm break-all">{item.campo}</p>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Valor antigo</p>
                              <pre className="text-sm whitespace-pre-wrap break-all font-sans">
                                {formatHistoryValue(item.campo, item.valorAntigo)}
                              </pre>
                            </div>
                            <div>
                              <p className="text-sm font-medium">Valor novo</p>
                              <pre className="text-sm whitespace-pre-wrap break-all font-sans">
                                {formatHistoryValue(item.campo, item.valorNovo)}
                              </pre>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2">
                        <pre className="text-sm whitespace-pre-wrap break-all">
                          {JSON.stringify(mod.details?.summary ?? {}, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                  {index < modificacoesFiltradas.length - 1 && <Separator className="my-4" />}
                </div>
              )
            })
          ) : (
            <p className="text-sm text-foreground">Nenhuma modificação encontrada para este tipo.</p>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
